import moment from 'moment';
import * as Yup from 'yup';
import BadWordsFilter from 'bad-words';
import UAParser from 'ua-parser-js';
import {
	TAR_PROCESS_TO_STATE_PROCESS_MAP_KEY,
	TAR_AGENT_ENUM,
	TAR_PROCESS_ENUM,
	PAYMENT_SELECTION_TYPES,
	insuranceReqTypes,
	BANK_INSURANCE_REQS,
	FINANCING_BANK_NAME_ENUM,
	HOW_IT_WORKS_SECTIONS_ENUM,
	HOW_IT_WORKS_SECTIONS,
	STRIPE_CREDIT_CARD_FEE_PERCENT,
	STRIPE_TRANSACTION_FEE_AMOUNT,
	EKHO_FINANCING_APPLICATION_CONSTANTS,
	abbrevStateNamesArr,
	FORMIK_DATE_FIELDS,
	SENSITIVE_DATA_FIELDS,
	EKHO_LOAN_TERM_OPTIONS,
	MAX_WEEKS_BEFORE_READY,
	DEFAULT_MIN_LOAN_AMOUNT,
	PAYMENT_TYPES,
	oemMap,
	DEV_OEM_KEY,
	STARK_OEM_KEY,
	VI_PROCESSING_FEE,
	PAPERWORK_FEE,
	CG_PROCESSING_FEE,
	INTERNAL_VEHICLE_TYPE_ENUM,
	STRIPE_PAYOUT_FEE_PERCENT,
	LENDER_DECISION_OPTIONS,
	financeOfferBuyerDecisionTypes,
	EKHO_FINANCING_STIPULATIONS_KEYS,
	INPUT_TYPES,
	NO_VIN_NEEDED_VEHICLE_TYPES,
	NO_ODOMETER_VEHICLE_TYPES,
	SARDINE_SESSION_KEY,
} from './constants';

// Legacy function to calculate the amount paid by the buyer before checkout
// Updated approach relies on payment receipts
export function calcAmountPaidBeforeCheckout(vehicle) {
	let total = 0;
	// Add amounts paid before checkout
	if (vehicle.paymentInfo?.amountsPaidBeforeCheckout?.length > 0) {
		vehicle.paymentInfo.amountsPaidBeforeCheckout.forEach((prevPayment) => {
			total += prevPayment.amount || 0;
		});
	}
	return total;
}

// Legacy function to calculate amount already paid by the buyer in total (inclusive amts paid to OEM and Ekho)
// Updated approach relies on payment receipts
export function calcAmountPaidByBuyer(vehicle) {
	let total = calcAmountPaidBeforeCheckout(vehicle);
	// Add total amounts received => sum across reservation fees, down payments, and cash payments
	total += vehicle.paymentInfo?.totalAmountReceived || 0;
	return total;
}

// Note that purchasedVehicle can be null, so we need to handle that case
export function getEkhoFinancingStipulation(purchasedVehicle, stipKey) {
	switch (stipKey) {
		case EKHO_FINANCING_STIPULATIONS_KEYS.CALL_FRF_TO_VERIFY_IDENTITY_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.CALL_FRF_TO_VERIFY_IDENTITY_STIP,
				name: 'Verify Identity via Phone Call',
				whatIsNeeded:
					'Have your cell phone handy to call the lender to verify your identity.',
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer: 'Please call the lender at 866-455-7623 to verify your identity.',
				fieldsToCollect: [
					{
						key: 'dividerOne',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Identity verification phone call',
						yup: Yup.string().notRequired(),
					},
					{
						// check_select confirming that the buyer has called the lender to verify their identity\
						key: 'identityVerificationCall',
						type: INPUT_TYPES.CHECK_SELECT,
						label: 'I have called the lender to verify my identity',
						yup: Yup.boolean().oneOf([true], 'Required'),
						title: 'I have called the lender to verify my identity',
						defaultValue: true,
					},
					{
						key: 'lenderContactName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Name of person you spoke to',
						yup: Yup.string().required('Required'),
						title: 'Name of person you spoke to',
					},
				],
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.PAYOFF_OUTSTANDING_LOAN_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.PAYOFF_OUTSTANDING_LOAN_STIP,
				name: 'Pay Off Outstanding Loan(s) with Lender',
				whatIsNeeded: 'Funds to pay off your outstanding loan(s) with the lender',
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer:
					'Please pay off any outstanding loan(s) with FreedomRoad Financial',
				fieldsToCollect: [
					{
						key: 'dividerOne',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Pay off outstanding loans',
						yup: Yup.string().notRequired(),
					},
					{
						// check_select confirming that the buyer has paid off any existing loans
						key: 'existingLoanPayoff',
						type: INPUT_TYPES.CHECK_SELECT,
						label: 'I have paid off any outstanding loans with FreedomRoad Financial',
						yup: Yup.boolean().oneOf([true], 'Required'),
						title: 'I have paid off any outstanding loans with FreedomRoad Financial',
						defaultValue: true,
					},
				],
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.NOTARIZED_CONTRACT_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.NOTARIZED_CONTRACT_STIP,
				name: 'Notarized Contract',
				whatIsNeeded: 'A notarized copy of the contract',
				requestFromBuyer: false,
				autoValidateAfterProvided: true,
				guidanceForBuyer: 'Please sign the contract in the presence of a notary public.',
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.PERSONAL_REFERENCES_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.PERSONAL_REFERENCES_STIP,
				name: 'Personal References',
				whatIsNeeded:
					'The names and phone numbers of two personal references (family, friends, etc.)',
				requestFromBuyer: true,
				autoValidateAfterProvided: true,
				guidanceForBuyer:
					'Please provide the names and phone numbers of two personal references.',
				fieldsToCollect: [
					{
						key: 'referenceOneDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Reference 1',
						yup: Yup.string().notRequired(),
					},
					{
						key: 'referenceOneName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Full name of reference 1',
						yup: Yup.string().required('Required'),
						title: 'Full name of reference 1',
					},
					{
						key: 'referenceOnePhone',
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'tel',
						label: 'Phone number of reference 1',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, ''); // Strip away all non-numeric characters
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Phone number of reference 1',
						mask: '(999) 999-9999',
					},
					// select input for relationship
					{
						key: 'referenceOneRelationship',
						type: INPUT_TYPES.SELECT,
						label: 'Relationship to reference 1',
						yup: Yup.string().required('Required'),
						title: 'Relationship to reference 1',
						defaultValue:
							EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS
								.PARENT,
						list: [
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
								label: 'Employer',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
								label: 'Friend',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
								label: 'Other',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
								label: 'Parent',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
								label: 'Relative',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
								label: 'Spouse',
							},
						],
					},
					{
						key: 'referenceTwoDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Reference 2',
						yup: Yup.string().notRequired(),
					},
					{
						key: 'referenceTwoName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Full name of reference 2',
						yup: Yup.string().required('Required'),
						title: 'Full name of reference 2',
					},
					{
						key: 'referenceTwoPhone',
						inputType: 'tel',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Phone number of reference 2',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, ''); // Strip away all non-numeric characters
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Phone number of reference 2',
						mask: '(999) 999-9999',
					},
					{
						key: 'referenceTwoRelationship',
						type: INPUT_TYPES.SELECT,
						label: 'Relationship to reference 2',
						yup: Yup.string().required('Required'),
						title: 'Relationship to reference 2',
						defaultValue:
							EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS
								.FRIEND,
						list: [
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
								label: 'Employer',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
								label: 'Friend',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
								label: 'Other',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
								label: 'Parent',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
								label: 'Relative',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
								label: 'Spouse',
							},
						],
					},
				],
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.FIVE_PERSONAL_REFERENCES_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.FIVE_PERSONAL_REFERENCES_STIP,
				name: 'Five Personal References',
				whatIsNeeded:
					'The names and phone numbers of five personal references (family, friends, etc.)',
				requestFromBuyer: true,
				autoValidateAfterProvided: true,
				guidanceForBuyer:
					'Please provide the names and phone numbers of five personal references.',
				fieldsToCollect: [
					// Reference 1
					{
						key: 'referenceOneDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Reference 1',
						yup: Yup.string().notRequired(),
					},
					{
						key: 'referenceOneName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Full name of reference 1',
						yup: Yup.string().required('Required'),
						title: 'Full name of reference 1',
					},
					{
						key: 'referenceOnePhone',
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'tel',
						label: 'Phone number of reference 1',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, '');
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Phone number of reference 1',
						mask: '(999) 999-9999',
					},
					{
						key: 'referenceOneRelationship',
						type: INPUT_TYPES.SELECT,
						label: 'Relationship to reference 1',
						yup: Yup.string().required('Required'),
						title: 'Relationship to reference 1',
						defaultValue:
							EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS
								.PARENT,
						list: [
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
								label: 'Employer',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
								label: 'Friend',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
								label: 'Other',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
								label: 'Parent',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
								label: 'Relative',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
								label: 'Spouse',
							},
						],
					},
					// Reference 2-5 (repeat the same pattern)
					// Reference 2
					{
						key: 'referenceTwoDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Reference 2',
						yup: Yup.string().notRequired(),
					},
					{
						key: 'referenceTwoName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Full name of reference 2',
						yup: Yup.string().required('Required'),
						title: 'Full name of reference 2',
					},
					{
						key: 'referenceTwoPhone',
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'tel',
						label: 'Phone number of reference 2',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, '');
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Phone number of reference 2',
						mask: '(999) 999-9999',
					},
					{
						key: 'referenceTwoRelationship',
						type: INPUT_TYPES.SELECT,
						label: 'Relationship to reference 2',
						yup: Yup.string().required('Required'),
						title: 'Relationship to reference 2',
						defaultValue:
							EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS
								.FRIEND,
						list: [
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
								label: 'Employer',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
								label: 'Friend',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
								label: 'Other',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
								label: 'Parent',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
								label: 'Relative',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
								label: 'Spouse',
							},
						],
					},
					// Reference 3
					{
						key: 'referenceThreeDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Reference 3',
						yup: Yup.string().notRequired(),
					},
					{
						key: 'referenceThreeName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Full name of reference 3',
						yup: Yup.string().required('Required'),
						title: 'Full name of reference 3',
					},
					{
						key: 'referenceThreePhone',
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'tel',
						label: 'Phone number of reference 3',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, '');
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Phone number of reference 3',
						mask: '(999) 999-9999',
					},
					{
						key: 'referenceThreeRelationship',
						type: INPUT_TYPES.SELECT,
						label: 'Relationship to reference 3',
						yup: Yup.string().required('Required'),
						title: 'Relationship to reference 3',
						defaultValue:
							EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS
								.RELATIVE,
						list: [
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
								label: 'Employer',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
								label: 'Friend',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
								label: 'Other',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
								label: 'Parent',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
								label: 'Relative',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
								label: 'Spouse',
							},
						],
					},
					// Reference 4
					{
						key: 'referenceFourDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Reference 4',
						yup: Yup.string().notRequired(),
					},
					{
						key: 'referenceFourName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Full name of reference 4',
						yup: Yup.string().required('Required'),
						title: 'Full name of reference 4',
					},
					{
						key: 'referenceFourPhone',
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'tel',
						label: 'Phone number of reference 4',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, '');
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Phone number of reference 4',
						mask: '(999) 999-9999',
					},
					{
						key: 'referenceFourRelationship',
						type: INPUT_TYPES.SELECT,
						label: 'Relationship to reference 4',
						yup: Yup.string().required('Required'),
						title: 'Relationship to reference 4',
						defaultValue:
							EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS
								.FRIEND,
						list: [
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
								label: 'Employer',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
								label: 'Friend',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
								label: 'Other',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
								label: 'Parent',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
								label: 'Relative',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
								label: 'Spouse',
							},
						],
					},
					// Reference 5
					{
						key: 'referenceFiveDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Reference 5',
						yup: Yup.string().notRequired(),
					},
					{
						key: 'referenceFiveName',
						type: INPUT_TYPES.TEXT_INPUT,
						label: 'Full name of reference 5',
						yup: Yup.string().required('Required'),
						title: 'Full name of reference 5',
					},
					{
						key: 'referenceFivePhone',
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'tel',
						label: 'Phone number of reference 5',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, '');
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Phone number of reference 5',
						mask: '(999) 999-9999',
					},
					{
						key: 'referenceFiveRelationship',
						type: INPUT_TYPES.SELECT,
						label: 'Relationship to reference 5',
						yup: Yup.string().required('Required'),
						title: 'Relationship to reference 5',
						defaultValue:
							EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS
								.OTHER,
						list: [
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER,
								label: 'Employer',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
								label: 'Friend',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.OTHER,
								label: 'Other',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
								label: 'Parent',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE,
								label: 'Relative',
							},
							{
								value: EKHO_FINANCING_APPLICATION_CONSTANTS
									.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE,
								label: 'Spouse',
							},
						],
					},
				],
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.MAX_FINANCED_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.MAX_FINANCED_STIP,
				name: 'Max Financed',
				requestFromBuyer: false,
				autoValidateAfterProvided: false,
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_RESIDENCE_STIP:
			const creditAppAddressObject =
				purchasedVehicle?.creditApplication?.applicantDetails || null;
			let creditAppAddress = 'the address you provided on your financing application.';
			if (creditAppAddressObject) {
				creditAppAddress = getConcatenatedAddressString(creditAppAddressObject);
				creditAppAddress += ` (which is the address you provided on your financing application)`;
			}
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_RESIDENCE_STIP,
				name: 'Proof of Residence',
				whatIsNeeded: `Please provide a copy of an internet bill, utility bill, credit card statement, phone bill, or bank statement dated within the last 30 days that shows your current address matching ${creditAppAddress}. Photos of the envelope containing the document are not acceptable. A government-issued ID, with the correct address on it, can also be used.`,
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer: `Please provide a copy of an internet bill, utility bill, credit card statement, phone bill, or bank statement dated within the last 30 days that shows your current address matching ${creditAppAddress}. Photos of the envelope containing the document are not acceptable. A government-issued ID, with the correct address on it, can also be used.`,
				uploadToCollect: {
					title: 'Provide proof of residence',
					description: `Please provide at least 1 copy of an internet bill, utility bill, credit card statement, phone bill, or bank statement dated within the last 30 days that shows your current address matching ${creditAppAddress}. Photos of the envelope containing the document are not acceptable. A government-issued ID, with the correct address on it, can also be used.`,
					minUploads: 1,
					exampleImgUrl:
						'https://storage.googleapis.com/ad-backend-prod.appspot.com/oems/QczG5nHitwkBiiQBFiLW/exampleUploads/utilityBill',
					exampleImgTitle: `Utility bill example upload`,
					exampleImgDescription: `Here is an example of a utility bill with the date and buyer's name and address`,
					uploadBlockTitles: ['Proof of residence 1', 'Proof of residence 2'],
				},
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_SSN_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_SSN_STIP,
				name: 'Proof of Social Security Number',
				whatIsNeeded:
					'A copy of your Social Security card or IRS documentation showing your SSN',
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer:
					'Please provide a copy of either your Social Security card or official IRS documentation that shows your Social Security Number.',
				uploadToCollect: {
					title: 'Provide proof of SSN',
					description:
						'Please provide at least 1 copy of either your Social Security card or official IRS documentation (such as a tax return or W-2) that clearly shows your Social Security Number.',
					minUploads: 1,
					uploadBlockTitles: ['Proof of SSN 1', 'Proof of SSN 2'],
				},
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_INCOME_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_INCOME_STIP,
				name: 'Proof of Income',
				whatIsNeeded:
					'A PDF (not screenshot) of your most recent pay stub reflecting your income, or the most recent three months of bank statements.',
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer:
					'Please provide a PDF of your most recent pay stub reflecting your income (screenshot not allowed). If you are unable to provide a pay stub, please provide the most recent three months of bank statements.',
				uploadToCollect: {
					title: 'Provide proof of income',
					description:
						'Please provide a PDF of your most recent pay stub reflecting your income (screenshot not allowed). If you are unable to provide a pay stub, please provide the most recent three months of bank statements.',
					minUploads: 1,
					exampleImgUrl:
						'https://storage.googleapis.com/ad-backend-prod.appspot.com/oems/QczG5nHitwkBiiQBFiLW/exampleUploads/payStub',
					exampleImgDescription: `Here is an example of a pay stub with the date and buyer's name and address`,
					exampleImgTitle: `Pay stub example upload`,
					uploadBlockTitles: [
						'Proof of income 1',
						'Proof of income 2',
						'Proof of income 3',
					],
				},
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.ADDITIONAL_INCOME_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.ADDITIONAL_INCOME_STIP,
				name: 'Additional Income',
				whatIsNeeded:
					'Additional income sources (e.g. spousal income, child support, etc.) and the amount of each.',
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer: `The personal income you reported in your original financing on it's own is not enough to qualify for financing. Please list any additional income that you would like to have considered as part of your financing application. If you have no additional income, please contact support before submitting.`,
				fieldsToCollect: [
					// For each type of additional incom in EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS, create a text input field for the income amount and a text area for the buyer to describe the income source
					{
						key: 'spousalIncomDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Spousal income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.SPOUSAL_INCOME}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Spousal income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Spousal income amount',
						text: 'If you have partner, please list their income here in dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.SPOUSAL_INCOME}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your spousal income',
						yup: Yup.string(),
						title: 'Please explain your spousal income',
					},
					{
						key: 'childSupportDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Child support income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.CHILD_SUPPORT}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Child support amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Child support amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.CHILD_SUPPORT}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your child support income',
						yup: Yup.string(),
						title: 'Please explain your child support income',
					},
					{
						key: 'disabilityDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Disability income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.DISABILITY}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Disability income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Disability income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.DISABILITY}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your disability income',
						yup: Yup.string(),
						title: 'Please explain your disability income',
					},
					{
						key: 'investmentDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Investment income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.INVESTMENT}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Investment income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Investment income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},

					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.INVESTMENT}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your investment income',
						yup: Yup.string(),
						title: 'Please explain your investment income',
					},
					{
						key: 'pensionDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Pension income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.PENSION}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Pension income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Pension income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.PENSION}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your pension income',
						yup: Yup.string(),
					},

					{
						key: 'rentalDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Child support income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.RENTAL}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Rental income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Rental income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.RENTAL}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your rental income',
						yup: Yup.string(),
					},
					{
						key: 'retirementDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Retirement income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.RETIREMENT_INCOME}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Retirement income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Retirement income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.RETIREMENT_INCOME}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your retirement income',
						yup: Yup.string(),
					},
					{
						key: 'socialSecurityDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Social security income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.SOCIAL_SECURITY}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Social security income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Social security income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.SOCIAL_SECURITY}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your social security income',
						yup: Yup.string(),
					},
					{
						key: 'alimonyDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Social security income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.ALIMONY}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Alimony income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Alimony income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.ALIMONY}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your alimony income',
						yup: Yup.string(),
					},
					{
						key: 'unemploymentDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Unemployment benefits income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.UNEMPLOYMENT}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Unemployment benefits income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Unemployment benefits income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.UNEMPLOYMENT}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your unemployment income',
						yup: Yup.string(),
					},
					{
						key: 'otherDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Other income',
						yup: Yup.string().notRequired(),
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.OTHER}Amount`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'number',
						label: 'Other income amount',
						yup: Yup.number()
							.min(0, 'Must be greater than 0')
							.required('Required - input 0 if N/A'),
						title: 'Other income amount',
						text: 'Dollars per month',
						defaultValue: 0,
					},
					{
						key: `${EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.OTHER}Explanation`,
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'text',
						label: 'Please explain your other income',
						yup: Yup.string(),
					},
					{
						key: 'noteToLenderDivider',
						type: INPUT_TYPES.TITLE_DIVIDER,
						label: '',
						title: 'Additional information',
						yup: Yup.string().notRequired(),
					},
					// Add a text area for note to lender
					{
						key: 'noteToLender',
						type: INPUT_TYPES.TEXT_AREA,
						label: 'Additional information',
						text: 'Please use this space to provide any additional information that will help us understand your additional income reporting.',
						yup: Yup.string().notRequired(),
						colWidthOverride: 'col-12',
					},
				],
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.PROVIDE_CURRENT_WORK_PHONE_NUMBER_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.PROVIDE_CURRENT_WORK_PHONE_NUMBER_STIP,
				name: 'Provide Current Work Phone Number',
				whatIsNeeded: 'The phone number for your current employer',
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer: 'Please provide the phone number for your current employer.',
				fieldsToCollect: [
					{
						key: 'currentWorkPhoneNumber',
						type: INPUT_TYPES.TEXT_INPUT,
						inputType: 'tel',
						label: 'Current work phone number',
						yup: Yup.string()
							.transform((value) => {
								const cleanNumber = value.replace(/\D/g, ''); // Strip away all non-numeric characters
								return cleanNumber;
							})
							.required('Required')
							.matches(
								/^[2-9]\d{2}[2-9]\d{2}\d{4}$/,
								'Enter a valid US phone number',
							),
						title: 'Current work phone number',
						mask: '(999) 999-9999',
					},
				],
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.DOWN_PAYMENT_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.DOWN_PAYMENT_STIP,
				name: 'Down Payment',

				requestFromBuyer: false,
				autoValidateAfterProvided: false,
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.FIRST_TIME_BUYER_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.FIRST_TIME_BUYER_STIP,
				name: 'First Time Buyer',
				requestFromBuyer: false,
				autoValidateAfterProvided: false,
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_EMPLOYMENT_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.PROOF_OF_EMPLOYMENT_STIP,
				name: 'Proof of Employment',
				whatIsNeeded:
					'A copy of your most recent pay stub or other document showing your income from the primary employement that your provided on your application.',
				requestFromBuyer: true,
				autoValidateAfterProvided: false,
				guidanceForBuyer:
					'Please provide a copy of your most recent pay stub or other document showing your income from the primary employement that your provided on your application.',
				uploadToCollect: {
					title: 'Provide proof of employment',
					description:
						'Please provide at least 1 copy of your most recent pay stub (from with the last 30 days) or other document showing your income from the primary employement that your provided on your application.',
					minUploads: 1,
					uploadBlockTitles: ['Proof of employment 1', 'Proof of employment 2'],
				},
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.VALID_DRIVERS_LICENSE_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.VALID_DRIVERS_LICENSE_STIP,
				name: 'Copy of Valid Drivers License',
				requestFromBuyer: false,
				autoValidateAfterProvided: false,
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.FULL_INSURANCE_COVERAGE_REQUIRED_STIP:
			return {
				key: 'FULL_INSURANCE_COVERAGE_REQUIRED_STIP',
				name: 'Full Insurance Coverage Required',
				whatIsNeeded: 'Proof of insurance',
				requestFromBuyer: false,
				autoValidateAfterProvided: true,
				guidanceForBuyer: 'Please provide proof of insurance.',
			};
		case EKHO_FINANCING_STIPULATIONS_KEYS.ITEMIZED_ACCESSORY_LIST_STIP:
			return {
				key: EKHO_FINANCING_STIPULATIONS_KEYS.ITEMIZED_ACCESSORY_LIST_STIP,
				name: 'Itemized Accessory List',
				requestFromBuyer: false,
				autoValidateAfterProvided: false,
			};
		default:
			console.error(`No stipulation found for key: ${stipKey}`);
			return null;
	}
}

export function getOemId(oemKey, brandKey) {
	let oemId;
	// if brandKey is provided, return the oemId associated with the oemKey and brandKey
	// or the brandKey directly if there is no entry in the oemMap for the brandKey (this is for testing environment purposes)
	if (brandKey) {
		oemId = oemMap[oemKey]?.[brandKey] || oemMap[brandKey];
	}
	// otherwise, if the oemMap entry for this oemKey is an object, return the oemId associated with the oemKey
	else if (typeof oemMap[oemKey] === 'object') {
		oemId = oemMap[oemKey][oemKey];
	}
	// otherwise, return the oemId associated with the oemKey directly
	else {
		oemId = oemMap[oemKey];
	}
	if (!oemId) {
		throw new Error(`Could not find oemId from oemKey: ${oemKey} and brandKey: ${brandKey}`);
	}
	return oemId;
}

export function getCheckoutBaseUrl(oem) {
	// Default to client's checkout domain if it exists.
	if (oem.subdomains?.client?.checkout) {
		return oem.subdomains.client.checkout;
	}
	// Otherwise use any definition of ekho's checkout domain.
	if (oem.subdomains?.ekho?.checkout) {
		return oem.subdomains.ekho.checkout;
	}
	if (oem.checkoutDomain) {
		return oem.checkoutDomain;
	}
	// fallback to the oemKey
	return `https://${oem._oemKey}-checkout.ekhodealer.com`;
}

export function getBuyerPortalBaseUrl(oem) {
	// Default to client's buyerPortal domain if it exists.
	if (oem.subdomains?.client?.buyerPortal) {
		return oem.subdomains.client.buyerPortal;
	}
	// Otherwise use any definition of ekho's buyerPortal domain.
	if (oem.subdomains?.ekho?.buyerPortal) {
		return oem.subdomains.ekho.buyerPortal;
	}
	if (oem.buyerPortalDomain) {
		return oem.buyerPortalDomain;
	}
	// fallback to the oemKey
	return `https://${oem._oemKey}.ekhodealer.com`;
}

export function getBrandKeysFromOemKey(oemKey) {
	let brandKeys;
	// if the oemMap entry for this oemKey is an object, return all of the keys in that object
	if (typeof oemMap[oemKey] === 'object') {
		brandKeys = Object.keys(oemMap[oemKey]);
	}
	// otherwise, return the oemKey as the only brandKey
	else {
		brandKeys = [oemKey];
	}
	return brandKeys;
}

export function getVehicleModelName(purchasedVehicle, oemKey) {
	switch (oemKey) {
		case DEV_OEM_KEY: {
			return 'VARG MX ALPHA 80HP';
		}
		case STARK_OEM_KEY: {
			const { specs } = purchasedVehicle;
			// stark varg
			// if there's a spec with id === 4521bc4c-2352-41c7-9b2f-8648112d8592, then return "VARG MX 60HP"
			if (specs.find((spec) => spec.id === '4521bc4c-2352-41c7-9b2f-8648112d8592')) {
				return 'VARG MX 60HP';
			}
			// if there's a spec with id === 8cd35c08-0cf3-4091-805e-d81b501ab2ab, then return "VARG MX ALPHA 80HP"
			if (specs.find((spec) => spec.id === '8cd35c08-0cf3-4091-805e-d81b501ab2ab')) {
				return 'VARG MX ALPHA 80HP';
			}
			if (specs.find((spec) => spec.id === '050be27c-03dc-49a9-88d9-dccf53e602ae')) {
				return 'VARG EX ALPHA 80HP';
			}
			if (specs.find((spec) => spec.id === 'ebe8dd88-4bff-48c3-8834-7bde2c3c44af')) {
				return 'VARG EX 60HP';
			}
			throw new Error("Invalid Stark vehicle model; couldn't determine model name");
		}
		default:
			return purchasedVehicle.name;
	}
}

export function getDeliveryEstFunc(oemKey, oemVehicle, deliveryStateAbbrev, deliveryDistance) {
	switch (oemKey) {
		case 'maeving': {
			// if invalid inputs, return null
			if (!oemVehicle || !deliveryStateAbbrev || deliveryDistance === null) {
				return { customDeliveryEstFunc: null, customFuncData: null };
			}
			const customFuncData = {
				vehicle: oemVehicle,
				deliveryState: deliveryStateAbbrev,
				deliveryDist: deliveryDistance,
			};
			const customFunc = (funcData) => {
				// eslint-disable-next-line no-unused-vars
				const { vehicle, deliveryState, deliveryDist } = funcData;
				// // special distance logic for CA
				// if (deliveryState === 'CA') {
				// 	if (deliveryDist <= 100 || vehicle.productId === '8135661125787') {
				// 		return 0;
				// 	}
				// 	return 250;
				// }
				// state granular logic for other states
				if (
					deliveryState &&
					vehicle.stateData?.[deliveryState] &&
					'deliveryEstimate' in vehicle.stateData[deliveryState]
				) {
					return vehicle.stateData[deliveryState]?.deliveryEstimate;
				}
				// default logic
				return vehicle.deliveryEstimate || 0;
			};
			return { customDeliveryEstFunc: customFunc, customFuncData };
		}
		default:
			return { customDeliveryEstFunc: null, customFuncData: null };
	}
}

// ---------- NEWER VERSION THAT USES ACCURATE DELIVERY FUNCTIONS ---------- //
// get vehicle delivery estimate by checking for state-specific delivery estimates
export function getVehicleDeliveryEstimate(
	oemVehicle,
	deliveryState,
	customDeliveryEstFunc = null,
	customFuncData = null,
) {
	let deliveryEstimate = null;
	// if provided a custom function, use that to calculate the delivery estimate
	if (customDeliveryEstFunc) {
		deliveryEstimate = customDeliveryEstFunc(customFuncData);
		if (deliveryEstimate !== null) {
			return deliveryEstimate;
		}
	}
	// if we have a registration state and a state-specific delivery estimate, use that estimate
	if (
		deliveryState &&
		oemVehicle.stateData?.[deliveryState] &&
		'deliveryEstimate' in oemVehicle.stateData[deliveryState]
	) {
		deliveryEstimate = oemVehicle.stateData[deliveryState]?.deliveryEstimate;
		// otherwise, use the default delivery estimate on the vehicle object
		// or 0 if that doesn't exist
	} else {
		deliveryEstimate = oemVehicle.deliveryEstimate || 0;
	}
	return deliveryEstimate;
}

export function getConcatenatedAddressString(addressObj) {
	if (!addressObj) {
		return '';
	}
	let addressString = addressObj.addressLineOne ? addressObj.addressLineOne : '';
	if ('addressLineTwo' in addressObj && addressObj.addressLineTwo) {
		addressString += ` ${addressObj.addressLineTwo}`;
	}
	if ('city' in addressObj && addressObj.city) {
		addressString += `, ${addressObj.city}`;
	}
	if ('state' in addressObj && addressObj.state) {
		addressString += `, ${addressObj.state}`;
	}
	if ('zip' in addressObj && addressObj.zip) {
		addressString += ` ${addressObj.zip}`;
	}
	return addressString;
}

export function getVehiclePickUpEstimate(
	oemVehicle,
	pickUpLocation,
	customPickUpEstFunction = null,
	customFuncData = null,
) {
	let pickUpEstimate = null;
	// if provided a custom function, use that to calculate the delivery estimate
	if (customPickUpEstFunction) {
		pickUpEstimate = customPickUpEstFunction(customFuncData);
		if (pickUpEstimate !== null) {
			return pickUpEstimate;
		}
	}
	const pickUpState = pickUpLocation?.state;
	// if we have a registration state and a state-specific delivery estimate, use that estimate
	if (
		pickUpState &&
		oemVehicle.stateData?.[pickUpState] &&
		'pickUpEstimate' in oemVehicle.stateData[pickUpState]
	) {
		pickUpEstimate = oemVehicle.stateData[pickUpState]?.pickUpEstimate;
		// otherwise, use the default delivery estimate on the vehicle object
		// or 0 if that doesn't exist
	} else {
		pickUpEstimate = oemVehicle.pickUpEstimate || 0;
	}
	return pickUpEstimate;
}

export function createBestTimeUntilString(numDays) {
	let waitTime = '';
	// averageProductionTime is in days
	// Convert to weeks or months if necessary
	if (numDays >= 84) {
		waitTime = `${Math.round(numDays / 30)} months`;
	} else if (numDays >= 28) {
		waitTime = `${Math.round(numDays / 7)} weeks`;
	} else if (numDays === 0) {
		waitTime = `No wait time!`;
	} else {
		// account for 1 day vs multiple days
		waitTime = `${numDays} day${numDays > 1 ? 's' : ''}`;
	}
	return waitTime;
}

export function getAutomatedCreditAppSubmissionData(financingBankName) {
	// AUTOMATED CREDIT APP SUBMISSION DATA
	const AUTOMATED_CREDIT_APP_DATA = {
		[FINANCING_BANK_NAME_ENUM.FRF]: {
			INCOME_SOURCE_MAPPING: {
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.ALIMONY]:
					'Alimony - Tax Exempt',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.CHILD_SUPPORT]:
					'Child Support - Tax Exempt',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.DISABILITY]: 'Disability',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.INVESTMENT]: 'Investment',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.PENSION]: 'Pension',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.RENTAL]: 'Rental',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.RETIREMENT_INCOME]:
					'Retirement Income',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.SOCIAL_SECURITY]:
					'Social Security',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.SPOUSAL_INCOME]:
					'Spousal Income',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.UNEMPLOYMENT]:
					'Unemployment',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.OTHER]: 'Other',
			},
			EMPLOYMENT_STATUS_MAPPING: {
				[EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.EMPLOYED]:
					'Employed',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.SELF_EMPLOYED]:
					'Self-Employed',
				Unemployed: 'Unemployed',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.RETIRED]: 'Retired',
				Other: 'Other',
			},
			HOUSING_TYPES_MAPPING: {
				[EKHO_FINANCING_APPLICATION_CONSTANTS.HOUSING_OWNERSHIP_OPTIONS.OWNED]: 'Own',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.HOUSING_OWNERSHIP_OPTIONS.RENTED]: 'Rent',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.HOUSING_OWNERSHIP_OPTIONS.FAMILY]: 'Other',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.HOUSING_OWNERSHIP_OPTIONS
					.LIVES_WITH_RELATIVES]: 'Other',
				Other: 'Other',
			},
			REFERENCE_RELATIONSHIP_MAPPING: {
				[EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.EMPLOYER]:
					'Employer',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND]:
					'Friends',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.OTHER]:
					'Other',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.PARENT]:
					'Parents',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.RELATIVE]:
					'Relative',
				[EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.SPOUSE]:
					'Spouse',
			},
			OEM_DATA: {
				// TEST OEM KEYS and BRAND KEYS: When testing, default to LAND's data
				'checkout-test-oem': {
					make: 'STARK',
					models: {
						8627130171685: (purchasedVehicle) => {
							getVehicleModelName(purchasedVehicle, 'bp-test-oem');
						},
					},
					modelSeries: {
						8627130171685: 'BASE', // This should eventually be a function that selects BASE/REAR BRAKE/SIDE STAND/SIDE STAND AND REAR BRAKE based on the specs.
					},
				},
				'bp-test-oem': {
					make: 'LAND',
					// Ekho Vehicle productId to FRF model name
					models: {
						8627130171685: (purchasedVehicle) => {
							getVehicleModelName(purchasedVehicle, 'bp-test-oem');
						},
						7514021134519: (purchasedVehicle) => {
							getVehicleModelName(purchasedVehicle, 'bp-test-oem');
						},
						8422437945647: (purchasedVehicle) => {
							getVehicleModelName(purchasedVehicle, 'bp-test-oem');
						},
						14: (purchasedVehicle) => {
							getVehicleModelName(purchasedVehicle, 'bp-test-oem');
						},
					},
				},
				'bp-demo-oem': {
					make: 'LAND',
					// Ekho Vehicle productId to FRF model name
					models: {
						8627130171685: 'DISTRICT STREET',
						7514021134519: 'DISTRICT STREET',
						9598934057244: 'DISTRICT STREET',
						8422437945647: 'DISTRICT SCRAMBLER',
						14: 'DISTRICT SCRAMBLER',
						9598925930780: 'DISTRICT SCRAMBLER',
					},
				},
				// END OF TEST OEM KEYS
				land: {
					make: 'LAND',
					// Ekho Vehicle productId to FRF model name
					models: {
						7142271713465: 'DISTRICT STREET',
						7656682553529: 'DISTRICT STREET',
						7657960472761: 'DISTRICT STREET',
						8015067054265: 'DISTRICT STREET',
						7174489309369: 'DISTRICT SCRAMBLER',
						7657959948473: 'DISTRICT SCRAMBLER',
						7657075310777: 'DISTRICT SCRAMBLER',
						9598925930780: 'DISTRICT SCRAMBLER',
					},
				},
				pickman: {
					make: 'KAIYUN MOTORS',
					// Ekho Vehicle productId to FRF model name
					models: {
						passenger: 'PICKMAN PASSENGER',
						classic: 'PICKMAN CLASSIC',
						'4xr': 'PICKMAN 4XR',
						xr: 'PICKMAN XR',
						mission: 'PICKMAN MISSION',
					},
				},
				maeving: {
					make: 'MAEVING',
					// Ekho Vehicle productId to FRF model name
					models: {
						7759622373531: 'RM1',
						8135661125787: 'RM1S',
						8135670661275: 'RM1S',
						8263930871963: 'RM1S',
					},
				},
				janus: {
					make: 'JANUS',
					// Ekho Vehicle productId to FRF model name
					models: {
						3945: 'HALCYON 450',
						49031: 'HALCYON 450',
						911: 'HALCYON 250',
						48994: 'HALCYON 250',
						892: 'GRYFFIN 250',
						49160: 'GRYFFIN 250',
						50523: 'GRYFFIN 450',
					},
				},
				moke: {
					make: 'MOKE INTERNATIONAL',
					// Ekho Vehicle productId to FRF model name
					models: {
						'moke-international-gen-1-ev': 'ELECTRIC MOKE',
					},
				},
				stark: {
					make: 'STARK',
					models: {
						'37dac47d-6cf5-4d96-9dc8-43328cc7717c': (purchasedVehicle) =>
							getVehicleModelName(purchasedVehicle, 'stark'),
						'45dac47d-6cf5-4d96-9dc8-43328cc7712a': 'VARG EX',
					},
					modelSeries: {
						'37dac47d-6cf5-4d96-9dc8-43328cc7717c': 'BASE', // This should eventually be a function that selects BASE/REAR BRAKE/SIDE STAND/SIDE STAND AND REAR BRAKE based on the specs.
					},
				},
				powerplus: {
					make: 'NEXTEM',
					models: {
						8208724492440: 'TOTE',
						8208727670936: 'TOTE',
						8208721805464: 'TOTE',
						8208735600792: 'TOTE JR.',
						8194354380952: 'TOTE',
						8208727015576: 'TOTE',
						8208737042584: 'RIDE',
						8208740876440: 'BURRO SB 4',
						8208756539544: 'BURRO LB 4',
						8208766959768: '8 PERSON',
						8208765419672: '6 PERSON',
						8208759062680: '4 PERSON',
					},
				},
				naxeon: {
					make: 'NAXEON',
					models: {
						iam: 'I.AM',
						'iam-lite': 'I.AM',
						'iam-pro': 'I.AM',
					},
				},
				arcticleopard: {
					make: 'ARCTIC LEOPARD',
					models: {
						9845749154108: 'EX800',
						9902468596028: 'EX700',
						9845981872444: 'EXE880',
						9902463648060: 'EXE800',
						9846209708348: 'EXT',
						'arcticleopard-et700': 'ET700',
						'arcticleopard-et600': 'ET600',
						'arcticleopard-eat600': 'EAT600',
						'arcticleopard-eat500': 'EAT500',
						9846099345724: 'XE PRO',
					},
				},
				ventus: {
					make: 'VENTUS',
					models: {
						'ventus-one': 'VENTUS ONE',
						9335050731804: 'VENTUS ONE',
					},
				},
				bakcou: {
					make: 'BAKCOU',
					models: {
						8276931575964: 'X22',
						8276931674268: 'X24',
					},
				},
				niu: {
					make: 'NIU',
					models: {
						7329554890812: 'XQI3',
						7830857318460: 'XQI3',
					},
				},
				pave: {
					make: 'PAVE MOTORS',
					models: {
						bk: 'BK',
						bku: 'BK-U',
					},
				},
				zero: {
					make: 'ZERO MOTORCYCLES',
					models: {
						// 'zero-xb': 'XB', pending
						// 'zero-xe': 'XE', pending
						zeroDsr2024: 'DSR',
						zeroDsr: 'DSR',
						zeroDs: 'DS',
						zeroSrS: 'SR/S',
						zeroSrF: 'SR/F',
						zeroS: 'S',
						zeroFxe: 'FXE',
						zeroFx: 'FX',
						zeroDsrX: 'DSR/X',
						zeroDsrX2024: 'DSR/X',
						zeroDs2024: 'DS',
						zeroSrS2024: 'SR/S',
						zeroS2024: 'S',
						zeroFxe2024: 'FXE',
						zeroFx2024: 'FX',
					},
				},
				massimo: {
					make: 'MASSIMO',
					models: {
						MSU8505: 'MSU850-5',
						TBOSS760: 'T-BOSS 760',
						MVRCARGOMAX: 'GOLF CART MVR CARGO MAX',
						MSA560: 'MSA560',
						TIRO480: 'TIRO 480',
						BUCK250: 'BUCK250 WITH HALF DOOR',
						MSU850: 'MSU850',
						BUCK450: 'BUCK 450 WITH SCREEN AND DOOR',
						TBOSS560X: 'T-BOSS 560X',
						TBOSS560L: 'T-BOSS 560L',
						TIRO400: 'TIRO 400',
						TIRO800: 'TIRO 800',
						TBOSS410X: 'T-BOSS 410X',
						BUCK450X: 'BUCK 450X WITH SCREEN AND DOOR',
						TBOSS410: 'T-BOSS 410',
						MGCCARGOMAX: 'MGC CARGO MAX',
						MSA450F: 'MSA450F',
						MSA760: 'MSA760',
						MGCETRIKEMINI: 'MGC E-TRIKE MINI',
						MGCETRIKE: 'MGC E-TRIKE',
						BUCK5506: 'BUCK 550-6',
						MSA210: 'MSA 210',
						MVRHVAC: 'MVR HVAC',
						WARRIOR1000MXD: 'WARRIOR 1000 MXD',
						WARRIOR1000MXDCREW: 'WARRIOR 1000 MXD CREW',
						TBOSS1000: 'T-BOSS 1000',
						MVR2X: 'GOLF CART MVR2X',
						TBOSS560: 'T-BOSS 560',
						BUCK250X: 'BUCK250X WITH HALF DOOR',
						MSA550: 'MSA550',
					},
				},
			},
		},
	};

	return AUTOMATED_CREDIT_APP_DATA[financingBankName];
}

export function getFullAddress(place) {
	let address1;
	let locality;
	let adminArea1Short;
	let adminArea1Long;
	let adminArea2Short;
	let adminArea2Long;
	let countryShort;
	let countryLong;
	let apartmentNumber;
	let postalCode = '';
	let postalCodeExtension;
	let sublocality;
	const formattedAddress = place.formatted_address;
	const latitude = place.geometry.location.lat();
	const longitude = place.geometry.location.lng();

	// Get each component of the address from the place details,
	place.address_components.forEach((component) => {
		const componentType = component.types[0];
		if (componentType === 'street_number') {
			address1 = component.long_name;
		}
		if (componentType === 'route') {
			address1 = `${address1 === undefined ? '' : address1} ${component.long_name}`;
		}
		if (componentType === 'locality') {
			locality = component.long_name;
		}
		if (componentType === 'administrative_area_level_1') {
			adminArea1Short = component.short_name;
			adminArea1Long = component.long_name;
		}
		if (componentType === 'administrative_area_level_2') {
			adminArea2Short = component.short_name;
			adminArea2Long = component.long_name;
		}
		if (componentType === 'postal_code') {
			postalCode = component.long_name;
		}
		if (componentType === 'postal_code_suffix') {
			postalCodeExtension = component.long_name;
		}
		if (componentType === 'country') {
			countryShort = component.short_name;
			countryLong = component.long_name;
		}
		if (componentType === 'subpremise') {
			apartmentNumber = component.long_name;
		}
		if (componentType === 'sublocality_level_1') {
			sublocality = component.long_name;
		}
	});

	const resAddress = {
		address1,
		locality,
		adminArea1Short,
		adminArea1Long,
		adminArea2Short,
		adminArea2Long,
		postalCode,
		postalCodeExtension,
		countryShort,
		countryLong,
		apartmentNumber,
		latitude,
		longitude,
		sublocality,
		formattedAddress,
	};
	return resAddress;
}

export function generateGoogleMapsAddress(addressObj) {
	if (addressObj.formattedAddress) {
		return addressObj.formatted_address;
	}
	// console.log('Address : ', addressObj);
	// Function to handle undefined values
	function formatValue(value) {
		return value !== undefined ? value : '';
	}

	// Create the address string
	let addressString = '';

	if (addressObj.address1) {
		addressString += formatValue(addressObj.address1);
	}

	if (addressObj.apartmentNumber) {
		addressString += `, ${formatValue(addressObj.apartmentNumber)}`;
	}

	if (addressObj.locality) {
		if (addressString.length > 0) {
			addressString += ', ';
		}
		addressString += formatValue(addressObj.locality);
	} else if (addressObj.sublocality) {
		if (addressString.length > 0) {
			addressString += ', ';
		}
		addressString += formatValue(addressObj.sublocality);
	}

	if (addressObj.adminArea1Short) {
		addressString += `, ${formatValue(addressObj.adminArea1Short)}`;
	}

	if (addressObj.postalCode) {
		addressString += ` ${formatValue(addressObj.postalCode)}`;
	}

	if (addressObj.countryLong) {
		addressString += `, ${formatValue(addressObj.countryLong)}`;
	}
	return addressString;
}

export function generateCityStateZipString(addressObj) {
	const { locality, adminArea1Short, postalCode } = addressObj;
	const localityPart = locality ? `${locality}` : '';
	const adminAreaPart = adminArea1Short ? `${locality ? ', ' : ''}${adminArea1Short}` : '';
	const postalCodePart = postalCode ? ` ${postalCode}` : '';
	return `${localityPart}${adminAreaPart}${postalCodePart}`.trim();
}

// Helper function for getting the supported states in the format we desire for a given OEM
export function getSupportedStates(oem) {
	if (!oem) {
		return [];
	}
	const { statesSupported } = oem;
	let toReturn = [];
	Object.entries(statesSupported).forEach(([key, value]) => {
		toReturn.push({ value: key, text: value.stateFullName });
	});
	toReturn = toReturn.sort((a, b) => a.text.localeCompare(b.text));
	return toReturn;
}

// Used in checkout to show the best possible term with the best APR
export function generateMonthlyPaymentEstimateObject(
	vehiclePrice,
	financingProgram,
	downPaymentRate = 0.15,
	disclaimerText = `Estimated payments are calculated by Ekho Dealer and are for informational purposes only. These estimates do not include title, registration fees, lien fees, or any other fees that may be imposed by a governmental agency in connection with the sale and financing of the vehicle. Other taxes may apply. They do not represent a financing offer or a guarantee of credit from the seller. Actual terms vary by lender.`,
	// if this is defined, it will override the downPaymentRate
	userDefinedDownPayment = null,
	// If this is defined, it will override the longest term in the financing program
	userDefinedTerm = null,
) {
	let downPayment = userDefinedDownPayment || vehiclePrice * downPaymentRate;
	// Check if downPayment is above the minimum down payment and below the vehicle price and adjust if necessary
	if (downPayment < 0) {
		downPayment = 0;
	} else if (downPayment > vehiclePrice) {
		downPayment = vehiclePrice;
	}
	const loanAmount = vehiclePrice - downPayment;
	// Assuming Tier 1 is the desired tier
	const tier1 = financingProgram.tiers.find((tier) => tier.tierNum === 1);
	const longestTerm = Math.max(...Object.keys(tier1.terms).map((num) => parseInt(num, 10)));
	let numMonths = longestTerm;
	let termDetails = tier1.terms[longestTerm];
	// If the user has defined a term that is not the longest term, use that term's details
	if (userDefinedTerm && tier1.terms[userDefinedTerm]) {
		termDetails = tier1.terms[userDefinedTerm];
		numMonths = userDefinedTerm;
	}
	const apr = termDetails.newVehicleApr;

	let monthlyPayment;

	if (apr === 0) {
		monthlyPayment = loanAmount / numMonths;
	} else {
		// Convert annual APR to monthly interest rate
		const monthlyInterestRate = apr / 12;
		// Calculate monthly payment
		monthlyPayment =
			(loanAmount * (monthlyInterestRate * (1 + monthlyInterestRate) ** numMonths)) /
			((1 + monthlyInterestRate) ** numMonths - 1);
	}

	const description = `Estimated monthly payment of $${monthlyPayment.toFixed(
		2,
	)}. Based on a vehicle price of $${vehiclePrice}, on a ${numMonths} month loan with $${downPayment.toFixed(
		2,
	)} down payment, and ${apr * 100}% APR`;

	return {
		monthlyPayment,
		description,
		term: numMonths,
		apr,
		downPayment,
		disclaimerText,
	};
}

// ------ KEEPING BECAUSE IT MIGHT BE USEFUL AT SOME POINT ------ //
export async function geocodeAddressAsync(address, apiKey) {
	const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
		address,
	)}&key=${apiKey}`;
	const response = await fetch(url);
	const data = await response.json();

	if (data.status === 'OK') {
		// Get the latitude and longitude from the response
		const latitude = data.results[0].geometry.location.lat;
		const longitude = data.results[0].geometry.location.lng;
		return { latitude, longitude };
	}
	throw new Error(`Geocoding failed: ${data.status}`);
}

export function getAdditionalDuesTotals(additionalDues, remitTaxesDirectly = false) {
	const feeTotal = additionalDues.feeEstimates.reduce(
		(acc, fee) => acc + (fee.value || fee.amount),
		0,
	);
	const taxTotal = additionalDues.taxEstimates.reduce(
		(acc, tax) => acc + (tax.value || tax.amount),
		0,
	);
	const internalFeeTotal = additionalDues.feeEstimates
		.filter((fee) => fee.feeType === 'vendorFee')
		.reduce((acc, fee) => acc + (fee.value || fee.amount), 0);
	const internalTaxTotal = remitTaxesDirectly ? taxTotal : 0;
	const internalTaxAndFeeTotal = internalFeeTotal + internalTaxTotal;
	const externalTaxAndFeeTotal = feeTotal + taxTotal - internalTaxAndFeeTotal;
	const additionalDuesTotal =
		feeTotal +
		taxTotal +
		// deliveryEstimate should always be 0 if buyer is picking up and pickUpEstimate should always be 0 if buyer is accepting delivery
		additionalDues.deliveryEstimate +
		(additionalDues.pickUpEstimate || 0);
	return {
		additionalDuesTotal,
		feeTotal,
		taxTotal,
		internalTaxAndFeeTotal,
		internalFeeTotal,
		internalTaxTotal,
		externalTaxAndFeeTotal,
	};
}

// function used to get the correct insurance object for a given state and tarProcess
export function getRelevantInsuranceObject(state, tarProcess) {
	const { insurance: defaultInsuranceData, tarProcesses } = state;
	const processKey = TAR_PROCESS_TO_STATE_PROCESS_MAP_KEY[tarProcess];
	const insuranceOverrides = tarProcesses[processKey].insurance;
	const insuranceData = { ...defaultInsuranceData, ...insuranceOverrides };
	return insuranceData;
}

export function determineTarAgent(purchasedVehicle) {
	const { registrationDetails } = purchasedVehicle;
	const { tarAgent } = registrationDetails;
	if (tarAgent === TAR_AGENT_ENUM.EKHO) {
		return TAR_AGENT_ENUM.VI;
	}
	return tarAgent;
}

export function determineTarProcess(purchasedVehicle) {
	const { registrationDetails } = purchasedVehicle;
	const { tarAgent } = registrationDetails;
	const tarProcess = purchasedVehicle.tarProcess
		? purchasedVehicle.tarProcess
		: registrationDetails.tarProcess;
	if (tarProcess) {
		return tarProcess;
	}
	if (tarAgent) {
		if (
			tarAgent === TAR_AGENT_ENUM.EKHO ||
			tarAgent === TAR_AGENT_ENUM.VI ||
			tarAgent === TAR_AGENT_ENUM.CG
		) {
			return TAR_PROCESS_ENUM.AUTOMATED;
		}
		if (tarAgent === TAR_AGENT_ENUM.BUYER || tarAgent === TAR_AGENT_ENUM.NONE) {
			return TAR_PROCESS_ENUM.ASSISTED;
		}
	}
	return TAR_PROCESS_ENUM.ASSISTED;
}

// Duplicate of funciton in Buyer Portal - should move to the component library
export function getAllLenderDecisions(vehicle) {
	// handle old data structure
	if (vehicle.paymentInfo?.nativeFinancing?.lenderDecision?.decision) {
		return {
			[FINANCING_BANK_NAME_ENUM.FRF]: vehicle.paymentInfo?.nativeFinancing?.lenderDecision,
		};
	}
	return vehicle.paymentInfo?.nativeFinancing?.lenderDecision ?? {};
}

export function hasLegacyFinancingSchema(purchasedVehicle) {
	return (
		purchasedVehicle.paymentInfo?.nativeFinancing?.lenderDecision?.decision !== undefined ||
		purchasedVehicle.creditApplication?.conditionalOffer !== undefined
	);
}

export function getBuyerSelectedLoanOffer(purchasedVehicle) {
	let toReturn = {
		bankName: null,
		loanOffer: null,
		path: null,
	};

	// if not financing, then no selected loan offer
	if (
		purchasedVehicle.paymentInfo?.paymentSelection !== PAYMENT_SELECTION_TYPES.nativeFinancing
	) {
		return toReturn;
	}

	// ---- Handle previous schema logic ---- //
	if (hasLegacyFinancingSchema(purchasedVehicle)) {
		// if not rejected and buyer hasn't made a decision yet OR has accepted it, then return the lender decision
		if (
			purchasedVehicle.paymentInfo?.nativeFinancing?.lenderDecision?.decision &&
			purchasedVehicle.paymentInfo?.nativeFinancing?.lenderDecision?.decision !==
				LENDER_DECISION_OPTIONS.rejected &&
			(!purchasedVehicle.paymentInfo?.nativeFinancing?.lenderDecision?.buyerOfferDecision ||
				purchasedVehicle.paymentInfo?.nativeFinancing?.lenderDecision
					?.buyerOfferDecision === financeOfferBuyerDecisionTypes.accepted)
		) {
			return {
				bankName: FINANCING_BANK_NAME_ENUM.FRF,
				loanOffer: purchasedVehicle.paymentInfo.nativeFinancing.lenderDecision,
				path: 'paymentInfo.nativeFinancing.lenderDecision',
			};
		}

		// if working on stip offer without clearence yet, then this is the selected loan offer
		if (
			purchasedVehicle.creditApplication?.conditionalOffer &&
			!purchasedVehicle.paymentInfo?.nativeFinancing?.lenderDecision?.decision
		) {
			return {
				bankName: FINANCING_BANK_NAME_ENUM.FRF,
				loanOffer: purchasedVehicle.creditApplication?.conditionalOffer,
				path: 'creditApplication.conditionalOffer',
			};
		}
	}

	// ---- Handle current schema logic ---- //
	else {
		Object.entries(purchasedVehicle.paymentInfo?.nativeFinancing?.lenderDecision ?? {}).forEach(
			([bankName, loanOffer]) => {
				if (loanOffer.buyerOfferDecision === financeOfferBuyerDecisionTypes.accepted) {
					toReturn = {
						bankName,
						loanOffer,
						path: `paymentInfo.nativeFinancing.lenderDecision.${bankName}`,
					};
				}
			},
		);
	}

	return toReturn;
}

// only need oemVehicle
export function lenderRequiresInsurance(bankName, purchasedVehicleTotalPrice, vehicleType) {
	const insuranceReqs = BANK_INSURANCE_REQS[bankName];

	if (!insuranceReqs || insuranceReqs[vehicleType] === undefined) {
		return false;
	}
	const vehicleReqs = insuranceReqs[vehicleType];
	const { required, minimumTotalPrice } = vehicleReqs;
	if (required && purchasedVehicleTotalPrice >= minimumTotalPrice) {
		return true;
	}
	return false;
}

// Helper function to determine the internal vehicle type based on the vehicleType and tarProcess. Relevant for converting dual sport vehicle types.
// This is a fairly naive implementation - it assumes that dual sport vehicles are motorcycles for automated and assisted processes; otherwise OHVs. This may need to be updated in the future (for support of mopeds for street legal variations, for example)
export function calcInternalVehicleType(vehicleType, tarProcess) {
	if (vehicleType === INTERNAL_VEHICLE_TYPE_ENUM.DUAL_SPORT) {
		if (tarProcess === TAR_PROCESS_ENUM.ASSISTED || tarProcess === TAR_PROCESS_ENUM.AUTOMATED) {
			return INTERNAL_VEHICLE_TYPE_ENUM.MOTORCYCLE;
		}
		return INTERNAL_VEHICLE_TYPE_ENUM.OHV;
	}
	return vehicleType;
}

export function getInsuranceReqEnum(
	currentVehicle,
	vehicleType,
	stateObject,
	overrideTarProcess = null,
) {
	let tarProcess;
	if (overrideTarProcess) {
		tarProcess = overrideTarProcess;
	} else {
		tarProcess = determineTarProcess(currentVehicle);
	}
	const internalVehicleType = calcInternalVehicleType(vehicleType, tarProcess);
	const insuranceReqObject = getRelevantInsuranceObject(stateObject, tarProcess);

	let insuranceRequiredForLender = false;
	const buyerIsFinancing =
		currentVehicle.paymentInfo?.paymentSelection === PAYMENT_SELECTION_TYPES.nativeFinancing;
	if (buyerIsFinancing) {
		const { bankName } = getBuyerSelectedLoanOffer(currentVehicle);
		insuranceRequiredForLender = lenderRequiresInsurance(
			bankName || FINANCING_BANK_NAME_ENUM.FRF, // if no currently selected loan, then default to FRF
			currentVehicle.totalPrice,
			internalVehicleType,
		);
	}

	// Check if insurance is required for lender
	if (
		insuranceRequiredForLender ||
		(insuranceReqObject?.optional === false && insuranceReqObject?.proofRequired === true)
	) {
		return insuranceReqTypes.validationRequired;
	}

	// Check if vehicle type is included in exempt types
	if (
		insuranceReqObject?.exemptVehicleTypes &&
		insuranceReqObject.exemptVehicleTypes.includes(internalVehicleType)
	) {
		return insuranceReqTypes.basicInformationRequired;
	}

	// Check if there are any fields that are required to be collected
	const hasMinFieldsToCollect = insuranceReqObject?.minFieldsToCollect?.length > 0;
	if (hasMinFieldsToCollect) {
		return insuranceReqTypes.basicInformationRequired;
	}

	// If none of the above, then insurance is not required
	return insuranceReqTypes.insuranceNotRequired;
}

export function getHowItWorksSections(
	isPreorder,
	purchasedVehicle,
	insuranceReqEnum,
	currentSection = HOW_IT_WORKS_SECTIONS_ENUM.ORDER,
	homeDelivery = true,
	overrideTarProcess = null,
) {
	let tarProcess;
	if (overrideTarProcess) {
		tarProcess = overrideTarProcess;
	} else {
		tarProcess = determineTarProcess(purchasedVehicle);
	}
	const sections = [];

	if (isPreorder) {
		sections.push(HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.PREORDER]);
		sections.push(HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.PREORDER_CONVERSION]);
	} else {
		sections.push(HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.ORDER]);
	}

	sections.push(HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.FINANCING]);

	let tarSection;
	switch (tarProcess) {
		case TAR_PROCESS_ENUM.AUTOMATED:
			tarSection = HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.TAR_PROCESS_AUTOMATED];

			break;
		case TAR_PROCESS_ENUM.ASSISTED:
			tarSection = HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.TAR_PROCESS_ASSISTED];

			break;
		case TAR_PROCESS_ENUM.AUTOMATED_TITLING:
			tarSection =
				HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.TAR_PROCESS_AUTOMATED_TITLING];

			break;
		case TAR_PROCESS_ENUM.ASSISTED_TITLING:
			tarSection =
				HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.TAR_PROCESS_ASSISTED_TITLING];

			break;
		default:
			break;
	}
	if (tarSection) {
		sections.push(tarSection);
	}

	let insuranceSection;
	if (
		insuranceReqEnum === insuranceReqTypes.validationRequired ||
		insuranceReqEnum === insuranceReqTypes.basicInformationRequired
	) {
		insuranceSection = HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.INSURANCE];
	}
	if (insuranceSection) {
		sections.push(insuranceSection);
	}

	sections.push(HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.PAPERWORK]);

	if (homeDelivery) {
		sections.push(HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.HOME_DELIVERY]);
	} else {
		sections.push(HOW_IT_WORKS_SECTIONS[HOW_IT_WORKS_SECTIONS_ENUM.PICK_UP]);
	}

	// loop through the sections and set the active section to the current section AND if section.order > currentSection.order, set section.completed = true
	const activeSection = HOW_IT_WORKS_SECTIONS[currentSection];
	sections.forEach((section) => {
		if (currentSection === section.id) {
			section.activeStep = true;
		} else {
			section.activeStep = false;
		}
		if (section.order < activeSection.order) {
			section.completed = true;
		} else if (section.order > activeSection.order) {
			section.completed = false;
		}
	});
	return sections;
}

export function formatHMMTime(time24) {
	const [hours, minutes] = time24.split(':');
	const hours12 = hours % 12 || 12; // convert hour to 12-hour format
	const amPm = hours >= 12 ? 'pm' : 'am';
	return `${hours12}:${minutes}${amPm}`;
}

export function createDateString(dateInMilliseconds) {
	const resultDate = new Date(dateInMilliseconds);
	return resultDate.toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});
}

export function supportIsAvailable(supportSchedule) {
	// Get the current date and time
	const now = new Date();

	// Get the current day of the week
	const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
	const currentDayIndex = now.getDay();

	if (Object.keys(supportSchedule).length === 0) {
		return { isAvailable: false, nextOnline: '' }; // If no schedule is defined
	}

	// eslint-disable-next-line no-plusplus
	for (let i = 0; i < 7; i++) {
		const dayIndex = (currentDayIndex + i) % 7;
		const day = days[dayIndex];
		const schedule = supportSchedule[day];

		if (schedule) {
			const startTime = schedule.start.split(':');
			const endTime = schedule.end.split(':');

			const startDate = new Date(
				now.getFullYear(),
				now.getMonth(),
				now.getDate() + i,
				parseInt(startTime[0], 10),
				parseInt(startTime[1], 10),
			);
			const endDate = new Date(
				now.getFullYear(),
				now.getMonth(),
				now.getDate() + i,
				parseInt(endTime[0], 10),
				parseInt(endTime[1], 10),
			);

			// Check if support is currently available
			if (i === 0 && now >= startDate && now <= endDate) {
				return { isAvailable: true, nextOnline: '' }; // Support is currently available
			}

			// Find the next available support time
			if (now < startDate) {
				const nextOnline = `Next available on ${
					day.charAt(0).toUpperCase() + day.slice(1)
				} at ${formatHMMTime(schedule.start)} EST`;
				return { isAvailable: false, nextOnline };
			}
		}
	}

	// If we get through the loop without finding available support
	return { isAvailable: false, nextOnline: '' };
}

// Helper function that calculates the stripe processing fees for a given amount
export function calcStripeProcessingFee(amount) {
	const fees =
		amount * STRIPE_CREDIT_CARD_FEE_PERCENT +
		STRIPE_TRANSACTION_FEE_AMOUNT * (1 + STRIPE_CREDIT_CARD_FEE_PERCENT);
	const roundedFees = Math.round(fees * 100) / 100;
	return roundedFees;
}

// Helper function that calculates the stripe payout fees for a given amount
export function calcStripePayoutFee(amount) {
	// finalAmt = amount / (1 + STRIPE_PAYOUT_FEE_PERCENT);
	const fees = amount - amount / (1 + STRIPE_PAYOUT_FEE_PERCENT);
	const roundedFees = Math.round(fees * 100) / 100;
	return roundedFees;
}

// helper function for rounding value to 2 decimal places
export function roundMoneyVal(value) {
	return Math.round(value * 100) / 100;
}

export function getDefaultCreditAppVals(
	buyer,
	buyerAddress,
	includePersonalReferences = false,
	includeLoanConfig = false,
) {
	const defaultInitialApplicantValues = {
		addressString: buyerAddress.addressString || '',
		ssn: '',
		email: buyer.email,
		phoneNumber: buyer.phone || '',
		addressLineOne: buyerAddress.addressLineOne,
		addressLineTwo: buyerAddress.addressLineTwo || '',
		city: buyerAddress.city,
		county: buyerAddress.county,
		state: buyerAddress.state,
		zip: buyerAddress.zip,
		maritalStatus: EKHO_FINANCING_APPLICATION_CONSTANTS.MARITAL_STATUS_OPTIONS.MARRIED,
		housingOwnership: EKHO_FINANCING_APPLICATION_CONSTANTS.HOUSING_OWNERSHIP_OPTIONS.OWNED,
		homeMoveInDate: '',
		monthlyAmt: '',
		noteToLender: '',
		homeAddressSameAsIdAddress: true,
	};
	if (includePersonalReferences) {
		defaultInitialApplicantValues.personalReferences = [
			{
				fullName: '',
				phoneNumber: '',
				relationship:
					EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.PARENT,
			},
			{
				fullName: '',
				phoneNumber: '',
				relationship:
					EKHO_FINANCING_APPLICATION_CONSTANTS.REFERENCE_RELATIONSHIP_OPTIONS.FRIEND,
			},
		];
	}
	if (includeLoanConfig) {
		// eslint-disable-next-line prefer-destructuring
		defaultInitialApplicantValues.loanTerm = EKHO_LOAN_TERM_OPTIONS[0];
		defaultInitialApplicantValues.downpaymentAmt = 0;
	}

	const defaulInitialEmploymentValues = {
		employmentStatus: EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.EMPLOYED,
		otherIncomes: [
			{
				source: EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.SPOUSAL_INCOME,
				amount: 0,
			},
			{
				source: EKHO_FINANCING_APPLICATION_CONSTANTS.INCOME_TYPE_OPTIONS.INVESTMENT,
				amount: 0,
			},
		],
	};
	return {
		defaultInitialApplicantValues,
		defaulInitialEmploymentValues,
	};
}

export function unixMillisecondsToDateString(unixMilliseconds) {
	return new Date(unixMilliseconds).toISOString().split('T')[0];
}

export function unixMillisecondsDateFormat(data, createLogFunc = null) {
	const formattedData = { ...data };
	FORMIK_DATE_FIELDS.forEach((field) => {
		if (Object.hasOwnProperty.call(data, field) && data[field] !== '') {
			let dateField = '';
			try {
				dateField = unixMillisecondsToDateString(data[field]); // input field will have format YYYY-MM-DD
			} catch (e) {
				if (createLogFunc) {
					createLogFunc(
						'Date-formatting-error',
						`Could not format date field ${field}`,
						'ERROR',
						{},
					);
				}
			}
			formattedData[field] = dateField;
		}
	});
	return formattedData;
}

export const FinancingApplicantDetailsValidation = (values) => {
	const errors = {};
	// if (!values.firstName) {
	// 	errors.firstName = 'Required';
	// }
	// if (!values.lastName) {
	// 	errors.lastName = 'Required';
	// }
	// DOB
	if (!values.dob) {
		errors.dob = 'Required';
	} else {
		let dob;
		// Check if the date is in MM/DD/YYYY format
		if (/^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(values.dob)) {
			dob = moment(values.dob, 'MM/DD/YYYY');
		}
		// Check if the date is in YYYY-MM-DD format
		else if (/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(values.dob)) {
			// eslint-disable-next-line no-unused-vars
			dob = moment(values.dob, 'YYYY-MM-DD');
		}
		// Format not valid
		else {
			errors.dob = 'Date must be in MM/DD/YYYY or YYYY-MM-DD format';
		}

		// Check if the person is at least 18 years old
		if (dob) {
			const minBornDate = moment().subtract(18, 'years');
			if (!dob.isSameOrBefore(minBornDate)) {
				errors.dob = 'You must be at least 18 years old to submit a credit application';
			}
		}
	}
	// phone number
	const cleanNumber = values.phoneNumber?.replace('+1 ', '').replaceAll('_', '');
	const onlyNumbers = cleanNumber?.replace(/\D/g, '');
	if (!onlyNumbers) {
		errors.phoneNumber = 'Required';
	} else {
		// const phoneRegex = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;
		// phone regex for 10 numbers
		const phoneRegex = /^\d{10}$/;
		if (!phoneRegex.test(onlyNumbers)) {
			errors.phoneNumber = 'Enter a valid US phone number';
		}
	}
	// SSN format checking
	if (!values.ssn) {
		errors.ssn = 'Required';
	} else if (!/^(\d{3}-?\d{2}-?\d{4}|XXX-XX-XXXX)$/.test(values.ssn)) {
		errors.ssn = 'Please ensure your SSN is in the correct format';
	}

	// Address
	if (!values.addressLineOne) {
		errors.addressLineOne = 'Required';
	}
	if (!values.city) {
		errors.city = 'Required';
	}
	if (!values.state) {
		errors.state = 'Required';
	} else if (abbrevStateNamesArr.includes(values.state) === false) {
		errors.state = 'Please select a valid US state';
	}
	if (!values.zip) {
		errors.zip = 'Required';
	} else if (!/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(values.zip)) {
		errors.zip = 'Please enter a valid US zip code';
	}
	// maritalStatus: '',
	if (!values.maritalStatus) {
		errors.maritalStatus = 'Required';
	} else {
		const options = ['Single', 'Married', 'Widowed', 'Divorced', 'Separated'];
		if (options.includes(values.maritalStatus) === false) {
			errors.maritalStatus = 'Please enter a valid marital status';
		}
	}
	// Email
	if (!values.email) {
		errors.email = 'Required';
	} else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
		errors.email = 'Please enter a valid email address';
	}

	// Housing Ownership
	if (!values.housingOwnership) {
		errors.housingOwnership = 'Required';
	} else {
		const options = ['Rented', 'Owned', 'Family', 'LivesWithRelatives', 'Other'];
		if (options.includes(values.housingOwnership) === false) {
			errors.housingOwnership = 'Please enter a valid housing ownership status';
		} else if (values.housingOwnership === 'Rented' && values.monthlyAmt === 0) {
			errors.housingOwnership =
				'Monthly rent must be greater than $0. Update the amount or choose the most applicable ownership type';
		}
	}

	// Home move in date
	if (!values.homeMoveInDate) {
		errors.homeMoveInDate = 'Required';
	} else {
		let moveInDate;
		// Check if the date is in MM/DD/YYYY format
		if (/^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(values.homeMoveInDate)) {
			moveInDate = moment(values.homeMoveInDate, 'MM/DD/YYYY');
		}
		// Check if the date is in YYYY-MM-DD format
		else if (/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(values.homeMoveInDate)) {
			moveInDate = moment(values.homeMoveInDate, 'YYYY-MM-DD');
		}
		// Format not valid
		else {
			errors.homeMoveInDate = 'Date must be in MM/DD/YYYY or YYYY-MM-DD format';
		}

		if (!moveInDate) {
			errors.homeMoveInDate = 'Date must be in MM/DD/YYYY or YYYY-MM-DD format';
		} else {
			// Check if the move-in date is in the past
			const currentDate = moment();
			if (!moveInDate.isSameOrBefore(currentDate)) {
				errors.homeMoveInDate = 'You cannot use a future date';
			}
		}
	}

	// Monthly Amt
	if (!values.monthlyAmt && values.monthlyAmt !== 0) {
		errors.monthlyAmt = 'Required';
	} else if (typeof values.monthlyAmt !== 'number') {
		errors.monthlyAmt = 'Value must be a number';
	} else if (values.monthlyAmt < 0) {
		errors.monthlyAmt = 'Value must be larger than or equal to 0';
	}

	// References
	if (values.references?.length > 0) {
		values.references?.forEach((reference, index) => {
			if (
				!reference.fullName ||
				reference.fullName.trim() === '' ||
				reference.fullName.trim().length < 2
			) {
				errors.references = errors.references || [];
				errors.references[index] = {
					...errors.references[index],
					fullName: 'Valid full name required',
				};
			}
			if (!reference.phoneNumber) {
				errors.references = errors.references || [];
				errors.references[index] = {
					...errors.references[index],
					phoneNumber: 'Required',
				};
			} else {
				const referenceCleanNumber = reference.phoneNumber
					?.replace('+1 ', '')
					.replaceAll('_', '');
				const referenceOnlyNumbers = referenceCleanNumber?.replace(/\D/g, '');
				if (!referenceOnlyNumbers) {
					errors.references = errors.references || [];
					errors.references[index] = {
						...errors.references[index],
						phoneNumber: 'Required',
					};
				} else {
					// const phoneRegex = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;
					// phone regex for 10 numbers
					const phoneRegex = /^\d{10}$/;
					if (!phoneRegex.test(referenceOnlyNumbers)) {
						errors.references = errors.references || [];
						errors.references[index] = {
							...errors.references[index],
							phoneNumber: 'Enter a valid US phone number',
						};
					}
				}
			}
			if (!reference.relationship) {
				errors.references = errors.references || [];
				errors.references[index] = {
					...errors.references[index],
					relationship: 'Required',
				};
			}
		});
	}

	// Validate preferred lending terms
	if ('loanTerm' in values && !EKHO_LOAN_TERM_OPTIONS.includes(values.loanTerm)) {
		errors.loanTerm = 'Invalid loan term';
	}

	const filter = new BadWordsFilter();
	// check that noteToLender is less than 2000 characters
	if (values.noteToLender?.length > 500) {
		errors.noteToLender = 'Keep under 500 characters';
		// please ensure it doesn't contain any bad words
	} else if (filter.isProfane(values.noteToLender || '')) {
		errors.noteToLender = 'Please ensure your note does not contain any inappropriate language';
	}

	return errors;
};

export const FinancingEmploymentDetailsValidation = (values) => {
	const errors = {};
	if (!values.employmentStatus) {
		errors.employmentStatus = 'Required';
	} else {
		const options = [
			EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.EMPLOYED,
			EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.SELF_EMPLOYED,
			EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.RETIRED,
		];
		if (options.includes(values.employmentStatus) === false) {
			errors.employmentStatus = 'Please enter a valid employment status';
		}
	}
	// monthlyGrossIncome
	if (!values.monthlyGrossIncome && values.monthlyGrossIncome !== 0) {
		errors.monthlyGrossIncome = 'Required';
	} else if (typeof values.monthlyGrossIncome !== 'number') {
		errors.monthlyGrossIncome = 'Value must be a number';
	} else if (values.monthlyGrossIncome < 0) {
		errors.monthlyGrossIncome = 'Value must be larger than or equal to 0';
	}

	// If employed, care about these fields
	if (
		values.employmentStatus ===
			EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.EMPLOYED ||
		values.employmentStatus ===
			EKHO_FINANCING_APPLICATION_CONSTANTS.EMPLOYMENT_STATUS_OPTIONS.SELF_EMPLOYED
	) {
		// Employer name
		if (!values.employerName) {
			errors.employerName = 'Required';
		}
		// phone number
		const cleanNumber = values.employerPhoneNumber?.replace('+1 ', '').replaceAll('_', '');
		if (!cleanNumber) {
			errors.employerPhoneNumber = 'Required';
		} else {
			const phoneRegex = /^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/;
			if (!phoneRegex.test(cleanNumber)) {
				errors.employerPhoneNumber = 'Enter a valid US phone number';
			}
		}

		// occupation
		if (!values.occupation) {
			errors.occupation = 'Required';
		}
		// jobStartDate
		if (!values.jobStartDate) {
			errors.jobStartDate = 'Required';
		} else {
			let jobStartDate;
			// Check if the date is in MM/DD/YYYY format
			if (/^(0[1-9]|1[012])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(values.jobStartDate)) {
				jobStartDate = moment(values.jobStartDate, 'MM/DD/YYYY');
			}
			// Check if the date is in YYYY-MM-DD format
			else if (/^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/.test(values.jobStartDate)) {
				jobStartDate = moment(values.jobStartDate, 'YYYY-MM-DD');
			}
			// Format not valid
			else {
				errors.jobStartDate = 'Date must be in MM/DD/YYYY or YYYY-MM-DD format';
			}

			if (!jobStartDate) {
				errors.jobStartDate = 'Date must be in MM/DD/YYYY or YYYY-MM-DD format';
			} else {
				// Check if the job start date date is in the past
				const currentDate = moment();
				if (!jobStartDate?.isSameOrBefore(currentDate)) {
					errors.jobStartDate = 'You cannot use a future date';
				}
			}
		}

		// Address
		if (!values.addressLineOne) {
			errors.addressLineOne = 'Required';
		}
		if (!values.city) {
			errors.city = 'Required';
		}
		if (!values.state) {
			errors.state = 'Required';
		} else if (abbrevStateNamesArr.includes(values.state) === false) {
			errors.state = 'Please select a valid US state';
		}
		if (!values.zip) {
			errors.zip = 'Required';
		} else if (!/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(values.zip)) {
			errors.zip = 'Please enter a valid US zip code';
		}
	}

	// otherIncomes
	if (values.otherIncomes?.length > 0) {
		values.otherIncomes?.forEach((income, index) => {
			if (!income.source) {
				errors.otherIncomes = errors.otherIncomes || [];
				errors.otherIncomes[index] = {
					...errors.otherIncomes[index],
					source: 'Required',
				};
			}
			if (typeof income.amount !== 'number' && income.amount !== '') {
				errors.otherIncomes = errors.otherIncomes || [];
				errors.otherIncomes[index] = {
					...errors.otherIncomes[index],
					amount: 'Value must be a number',
				};
			} else if (income.amount <= 0) {
				errors.otherIncomes = errors.otherIncomes || [];
				errors.otherIncomes[index] = {
					...errors.otherIncomes[index],
					amount: 'Value must be larger than 0',
				};
			}
		});
	}
	return errors;
};

export function dateFormatData(data) {
	const formattedData = { ...data };
	FORMIK_DATE_FIELDS.forEach((field) => {
		if (Object.hasOwnProperty.call(data, field)) {
			const dateField = new Date(data[field]).getTime(); // input field will have format YYYY-MM-DD
			formattedData[field] = dateField;
		}
	});
	return formattedData;
}

// This function converts a base64-encoded string to an ArrayBuffer
function base64ToArrayBuffer(base64) {
	const binaryString = window.atob(base64);
	const len = binaryString.length;
	const bytes = new Uint8Array(len);
	for (let i = 0; i < len; i += 1) {
		bytes[i] = binaryString.charCodeAt(i);
	}
	return bytes.buffer;
}

// This function converts a string to an ArrayBuffer
function str2ab(str) {
	const buf = new ArrayBuffer(str.length);
	const bufView = new Uint8Array(buf);
	for (let i = 0, strLen = str.length; i < strLen; i += 1) {
		bufView[i] = str.charCodeAt(i);
	}
	return buf;
}

// This function converts a PEM key to a raw key
async function pemToRawKey(pem) {
	// Remove the "BEGIN PUBLIC KEY" and "END PUBLIC KEY" lines and any line breaks
	const base64 = pem
		.replace('-----BEGIN PUBLIC KEY-----', '')
		.replace('-----END PUBLIC KEY-----', '')
		.replace(/\n/g, '');
	// Convert the base64-encoded key to an ArrayBuffer
	const arrayBuffer = base64ToArrayBuffer(base64);
	// Import the key as a raw key
	const rawKey = await window.crypto.subtle.importKey(
		'spki',
		arrayBuffer,
		{
			name: 'RSA-OAEP',
			hash: 'SHA-256',
		},
		true,
		['encrypt'],
	);
	return rawKey;
}

// Use the function to convert the PEM key to a raw key
// const pem = process.env.REACT_APP_ENCRYPTION_KEY; // replace with your public key

async function encrypt(data, pem) {
	const rawKey = await pemToRawKey(pem);
	const arrayBuffer = str2ab(data);
	const encryptedData = await window.crypto.subtle.encrypt(
		{
			name: 'RSA-OAEP',
		},
		rawKey,
		arrayBuffer,
	);
	// Convert to base64 string to save in backend
	return btoa(String.fromCharCode.apply(null, new Uint8Array(encryptedData)));
}

export async function encryptData(dataToEncrypt, pem) {
	const encryptedData = { ...dataToEncrypt };
	await Promise.all(
		SENSITIVE_DATA_FIELDS.map(async (field) => {
			if (Object.hasOwnProperty.call(dataToEncrypt, field)) {
				const encryptedField = await encrypt(String(dataToEncrypt[field]), pem);
				encryptedData[field] = encryptedField;
			}
		}),
	);
	return encryptedData;
}

// Check if vehicle delivery is within 8 weeks
export function buyerIsAbleToSubmitCreditApp(currentVehicle, auth, createLog) {
	try {
		const { estimatedDeliveryDate } = currentVehicle;
		// If vehicle is ready in more than 8 weeks
		const maxReadyDate = moment().add(MAX_WEEKS_BEFORE_READY, 'weeks');
		const readyByDate = moment(estimatedDeliveryDate);
		if (readyByDate.isSameOrAfter(maxReadyDate, 'date')) {
			// calculate number of days maxReadyDate is before readyByDate
			const daysUntilSubmission = readyByDate.diff(maxReadyDate, 'days');

			createLog(
				'Blocking-event',
				`Buyer submitted credit app but we are not ready to process it yet. They must wait ${daysUntilSubmission} days. Implement infra to auto submit this thing.`,
				'CRITICAL',
				{
					purchasedVehicleId: currentVehicle.id,
					placedOrderId: currentVehicle.placedOrderId,
					buyerID: auth.currentUser.uid,
				},
			);
			return { canSubmitCreditApp: false, daysUntilSubmission };
		}
		return { canSubmitCreditApp: true, daysUntilSubmission: 0 };
	} catch (error) {
		createLog(
			'Blocking-error',
			`Error when trying to determine if buyer can submit credit app: ${error}`,
			'CRITICAL',
			{
				purchasedVehicleId: currentVehicle.id,
				placedOrderId: currentVehicle.placedOrderId,
				buyerID: auth.currentUser.uid,
			},
		);
		// We return false here because user will be routed to the page indicating that they have to wait rather than having the site crash
		return { canSubmitCreditApp: false, daysUntilSubmission: 'UNKNOWN' };
	}
}

export function fetchDeviceInfo() {
	const parser = new UAParser();
	const result = parser.getResult();
	// Custom logic needed for device.type RE https://github.com/faisalman/ua-parser-js/issues/182
	if (!result.device.type) {
		if (result.os.name === 'iOS' || result.os.name === 'Android') {
			result.device.type = 'mobile';
		} else {
			result.device.type = 'pc';
		}
	}
	return result;
}

// Common analytics funcs used in BP and Checkout
// eslint-disable-next-line camelcase
export async function track_credit_application_created_by_buyer(data) {
	// Declaring this function as async so that it works with the confirm details page
	const { currentVehicle, oemProfileProps, registeredUserProps, daysUntilSubmission, windowObj } =
		data;
	const daysSinceDatePurchased = Math.floor(
		(Date.now() - new Date(currentVehicle.datePurchased)) / 86400000,
	);
	const { totalPrice } = currentVehicle;
	const sourceData = fetchDeviceInfo();
	windowObj.analytics.track('credit_application_created_by_buyer', {
		oemKey: oemProfileProps._oemKey,
		purchasedVehicleId: currentVehicle.id,
		purchasedVehicleName: currentVehicle.name,
		numBoltOnAccessories: currentVehicle.boltOnAccessories.length,
		daysSinceDatePurchased,
		vin: currentVehicle.vin ? currentVehicle.vin : 'not_yet_provided',
		vehicleStage: currentVehicle.stage,
		orderId: currentVehicle.placedOrderId,
		amountFinanced: totalPrice,
		buyerId: registeredUserProps.id,
		buyerEmail: registeredUserProps.email,
		buyerName: registeredUserProps.fullName,
		daysUntilSubmission,
		sourceData,
	});
	// Return because we call this as an async function and check for a return value in the confirm details page
	return {};
}

export function calcFinancingDownPaymentBounds(
	financingProgram,
	totalToFinance,
	oem,
	createLog,
	alreadyPaid = 0,
	buyerName = 'Unknown',
	checkoutCartId = 'Unknown',
) {
	// Calc max downpayment
	const minLoanAmt = financingProgram?.minimumLoanAmount ?? DEFAULT_MIN_LOAN_AMOUNT;
	const minimumLoanAmount = totalToFinance >= minLoanAmt ? minLoanAmt : totalToFinance;
	const maxDownPayment =
		totalToFinance > minimumLoanAmount ? totalToFinance - minimumLoanAmount : 0;

	// Calc min downpayment
	let minDownPayment = alreadyPaid;
	if (oem.paymentConfig?.[PAYMENT_TYPES.LOAN_ADDITIONAL_DOWN]?.minAdditionalDown) {
		minDownPayment = Math.max(
			alreadyPaid,
			oem.paymentConfig?.[PAYMENT_TYPES.LOAN_ADDITIONAL_DOWN]?.minAdditionalDown,
		);
	}
	if (minDownPayment > maxDownPayment || alreadyPaid > maxDownPayment) {
		createLog(
			'Invalid-downpayment-bounds',
			`Invalid bounds: min downpayment: ${minDownPayment}, max downpayment ${maxDownPayment}, already paid: ${alreadyPaid}`,
			'CRITICAL',
			{
				oemKey: oem.key,
				buyerName,
				checkoutCartId,
			},
		);
	}
	return { minDownPayment, maxDownPayment };
}

export function shouldSeeCreditAppInCheckout(numVehicles, paymentSelection, dueToday) {
	if (
		numVehicles === 1 &&
		paymentSelection === PAYMENT_TYPES.LOAN_DOWNPAYMENT &&
		dueToday === 0
	) {
		return true;
	}
	return false;
}

/**
 * Given an oemVehicle object and a state object, this function will
 * return an array of supported titling and registration processes.
 *
 * @param {Object} oemVehicle - The vehicle object that contains details such as
 * vehicle type, fuel type, and registration configuration.
 * @param {Object} stateObject - The state-specific data object that includes the
 * supported and unsupported titling and registration processes.
 *
 * @returns {Array} - An array of supported titling and registration processes.
 */
export function supportedTarProcesses(oemVehicle, stateObject) {
	const result = [];
	const { vehicleType, fuelType, registrationConfig } = oemVehicle;
	const {
		automatedTARSupported,
		automatedTitlingSupported,
		assistedTARNotSupported,
		assistedTitlingNotSupported,
		noneSupported,
	} = stateObject.tarProcesses;

	// Check if registration configuration is available
	if (!registrationConfig) {
		return [];
	}

	// Go through each TAR process: AUTOMATED, ASSISTED, AUTOMATED_TITLING, ASSISTED_TITLING NONE
	Object.values(TAR_PROCESS_ENUM).forEach((process) => {
		let supportedVehicleType = false;
		let supportedFuelType = false;
		switch (process) {
			case TAR_PROCESS_ENUM.AUTOMATED:
				supportedVehicleType = automatedTARSupported.vehicleTypes.includes(vehicleType);
				supportedFuelType = automatedTARSupported.fuelTypes.includes(fuelType);
				break;
			case TAR_PROCESS_ENUM.AUTOMATED_TITLING:
				supportedVehicleType = automatedTitlingSupported.vehicleTypes.includes(vehicleType);
				supportedFuelType = automatedTitlingSupported.fuelTypes.includes(fuelType);
				break;
			case TAR_PROCESS_ENUM.ASSISTED:
				supportedVehicleType = !assistedTARNotSupported.vehicleTypes.includes(vehicleType);
				supportedFuelType = !assistedTARNotSupported.fuelTypes.includes(fuelType);
				break;
			case TAR_PROCESS_ENUM.ASSISTED_TITLING:
				supportedVehicleType =
					!assistedTitlingNotSupported.vehicleTypes.includes(vehicleType);
				supportedFuelType = !assistedTitlingNotSupported.fuelTypes.includes(fuelType);
				break;
			case TAR_PROCESS_ENUM.NONE:
				supportedVehicleType = noneSupported.vehicleTypes.includes(vehicleType);
				supportedFuelType = noneSupported.fuelTypes.includes(fuelType);
				break;
			default:
		}
		// Check that both the vehicle type and fuel type are supported (OEM-specific)
		// and also that the process is supported in the registration configuration (state-specific)
		if (supportedVehicleType && supportedFuelType && registrationConfig[process]) {
			result.push(process);
		}
	});
	return result;
}

// Helper function for determining if an alert modal needs to be shown
export function showAlertModal(currentVehicle) {
	if (currentVehicle && currentVehicle.modals?.length) {
		const modalObj = currentVehicle.modals[0];
		if (!modalObj.acknowledged) {
			return true;
		}
	}
	return false;
}

export function calcEkhoProcessingFee(
	tarProcess,
	stateVariableFee,
	tarAgent,
	processFeeOverride = null,
) {
	let toReturn =
		// eslint-disable-next-line no-nested-ternary
		tarAgent === TAR_AGENT_ENUM.VI
			? VI_PROCESSING_FEE + PAPERWORK_FEE + stateVariableFee
			: tarProcess === TAR_PROCESS_ENUM.AUTOMATED
			? CG_PROCESSING_FEE + PAPERWORK_FEE
			: CG_PROCESSING_FEE;
	if (processFeeOverride && tarAgent in processFeeOverride) {
		toReturn = processFeeOverride[tarAgent].amount ?? toReturn;
	}
	return toReturn;
}

// Helper function to determine relevant payment config options. Used in checkout (with checkout carts) and in BP (with purchased vehicles) and backend
export function getPaymentConfigOptions(
	oem, // single object
	vehicles = null, // array of oemVehicles or single oemVehicle object
	checkoutPaymentOverride = null, // single object, if preorder should be preorderPaymentConfig. Otherwise, should be paymentConfig
	isPreorderCheckout = false,
) {
	if (isPreorderCheckout) {
		return checkoutPaymentOverride || oem.preorderPaymentConfig;
	}

	// 1. Get the payment config based on vehicle, OEM
	let { paymentConfig } = oem;
	if (vehicles) {
		// Ensure `vehicles` is treated as an array for consistency
		const vehiclesArray = Array.isArray(vehicles) ? vehicles : [vehicles];

		// Create a superset of the payment options based on the vehicles in the cart
		paymentConfig = vehiclesArray.reduce((acc, vehicle) => {
			const config = vehicle.paymentConfig || oem.paymentConfig;
			Object.keys(config).forEach((key) => {
				if (!acc[key]) {
					acc[key] = config[key];
				}
			});
			return acc;
		}, {});
	}

	// 2. Check to see if there is payment override in checkoutCart
	if (checkoutPaymentOverride) {
		// Validate that the options are a subset of associated payment options
		const overrideOptions = Object.keys(checkoutPaymentOverride);
		const paymentConfigOptions = Object.keys(paymentConfig);
		const isSubset = overrideOptions.every((option) => paymentConfigOptions.includes(option));
		if (!isSubset) {
			// return oem configured payment options by default
			return paymentConfig;
		}
		return checkoutPaymentOverride;
	}
	// 2. If no payment override, return the OEM configured payment options
	return paymentConfig;
}

export function getMostCompetitiveFinancingProgram(
	relevantFinancingPrograms,
	onlyPromotions = false,
) {
	// If no programs exist, return null
	if (!relevantFinancingPrograms || Object.keys(relevantFinancingPrograms).length === 0) {
		return null;
	}

	const toIterate = onlyPromotions
		? Object.values(relevantFinancingPrograms).filter((program) => {
				return program.promotionMetadata;
		  })
		: Object.values(relevantFinancingPrograms);

	const promoPrograms = toIterate.filter((program) => !!program.promotionMetadata);

	if (promoPrograms.length > 0) {
		const promoProgram = promoPrograms[0];

		// Find the longest term available in this program
		let longestTerm = 0;
		let aprForLongestTerm = 0;

		promoProgram.tiers.forEach((tier) => {
			Object.entries(tier.terms).forEach(([term, termDetails]) => {
				const termNum = Number(term);
				if (termNum > longestTerm) {
					longestTerm = termNum;
					aprForLongestTerm = termDetails.newVehicleApr;
				}
			});
		});

		return {
			...promoProgram,
			lowestApr: aprForLongestTerm,
			bestTerm: longestTerm,
		};
	}

	// If no promo, find the program with the longest term
	return toIterate.reduce((best, program) => {
		// Find the longest term in this program
		let longestTerm = 0;
		let aprForLongestTerm = 0;

		program.tiers.forEach((tier) => {
			Object.entries(tier.terms).forEach(([term, termDetails]) => {
				const termNum = Number(term);
				if (termNum > longestTerm) {
					longestTerm = termNum;
					aprForLongestTerm = termDetails.newVehicleApr;
				}
			});
		});

		// Compare with current best
		if (!best || longestTerm > best.bestTerm) {
			return {
				...program,
				lowestApr: aprForLongestTerm,
				bestTerm: longestTerm,
			};
		}

		return best;
	}, null);
}

// backwards compatible function for fetching creditAppRecipients
export function getCreditAppRecipients(purchasedVehicle) {
	if (
		purchasedVehicle.creditApplication &&
		'creditAppRecipients' in purchasedVehicle.creditApplication
	) {
		return purchasedVehicle.creditApplication.creditAppRecipients;
	}
	// if creditAppRecipients doesn't exist, then old version and only FRF would be the recipient
	if (purchasedVehicle.creditApplication) {
		return [FINANCING_BANK_NAME_ENUM.FRF];
	}
	// otherwise, return an empty array
	return [];
}

// Helper function for getting lender decision object based on lender name
// If return path str is set, then return path to where the object lives on the PV object
// Otherwise, return the actual object itself
// Backwards compat for old schema
export function getLenderDecisionObj(purchasedVehicle, bankName, returnPathStr = false) {
	if (!purchasedVehicle || !bankName) {
		return null;
	}
	const { paymentInfo } = purchasedVehicle;
	// check original location for backwards compatibility
	const decision = paymentInfo?.nativeFinancing?.lenderDecision?.decision;
	if (decision && bankName === FINANCING_BANK_NAME_ENUM.FRF) {
		if (returnPathStr) return 'paymentInfo.nativeFinancing.lenderDecision';
		return paymentInfo?.nativeFinancing?.lenderDecision;
	}
	if (returnPathStr) return `paymentInfo.nativeFinancing.lenderDecision.${bankName}`;
	return paymentInfo?.nativeFinancing?.lenderDecision?.[bankName] ?? null;
}

// function that returns true if the vehicle needs a vin
// for now this is synonymous with a vehicle that needs/has an MSO
export function vehicleNeedsVin(vehicle) {
	return !NO_VIN_NEEDED_VEHICLE_TYPES.includes(vehicle.vehicleType);
}
export function isMissingVin(vehicle) {
	if (vehicleNeedsVin(vehicle)) {
		return !vehicle.vin;
	}
	return false;
}

export function vehicleNeedsOdomReading(vehicle) {
	return !NO_ODOMETER_VEHICLE_TYPES.includes(vehicle.vehicleType);
}

export function isMissingOdomReading(vehicle) {
	if (vehicleNeedsOdomReading(vehicle)) {
		return !vehicle.odometerReading;
	}
	return false;
}

// Cross-platform hashEmail function
export async function hashEmail(email) {
	const normalized = email.toLowerCase().trim();
	const encoder = new TextEncoder();
	const data = encoder.encode(normalized);

	// In Node.js, polyfill crypto.subtle
	if (typeof globalThis.crypto?.subtle === 'undefined') {
		const { webcrypto } = await import('crypto');
		globalThis.crypto = webcrypto;
	}

	const hashBuffer = await globalThis.crypto.subtle.digest('SHA-256', data);
	const uint8Array = new Uint8Array(hashBuffer);
	const base64 = btoa(String.fromCharCode(...uint8Array));

	return base64.slice(0, 32);
}

// Add secure session helper functions
export const secureSessionStorage = {
	// Get the session with validation
	getSession: () => {
		try {
			const storedSession = localStorage.getItem(SARDINE_SESSION_KEY);
			if (!storedSession) return null;

			const parsedSession = JSON.parse(storedSession);

			// Validate session structure
			if (!parsedSession || !parsedSession.sessionKey) {
				secureSessionStorage.clearSession();
				return null;
			}

			// Check expiry
			if (parsedSession.expiresAt) {
				const expiryTime = new Date(parsedSession.expiresAt).getTime();
				const currentTime = new Date().getTime();

				if (currentTime >= expiryTime) {
					secureSessionStorage.clearSession();
					return null;
				}
			}

			return parsedSession;
		} catch (error) {
			// In case of any errors, clear the session and return null
			secureSessionStorage.clearSession();
			return null;
		}
	},

	// Save session with proper encoding
	saveSession: (sessionData) => {
		if (!sessionData || !sessionData.sessionKey) {
			return false;
		}

		try {
			localStorage.setItem(SARDINE_SESSION_KEY, JSON.stringify(sessionData));
			return true;
		} catch (error) {
			return false;
		}
	},

	// Clear session data
	clearSession: () => {
		localStorage.removeItem(SARDINE_SESSION_KEY);
	},
};
